import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { getToken } from '../lib/auth';
import { Icon } from '../lib/icons';

interface HealthData {
  status: string;
  service: string;
  version: string;
  environment: string;
}

/**
 * 技术特性说明。
 * 全部基于可验证的技术事实，不使用「永久」这类无法兑现的承诺。
 */
const TECH_FEATURES = [
  {
    icon: 'globe',
    title: '全球边缘加速',
    desc: '330 余座城市的边缘节点就近响应，亲友无论身处何地，打开纪念馆都同样迅速',
  },
  {
    icon: 'bolt',
    title: '无服务器架构',
    desc: '不依赖任何固定机器，流量高峰自动扩容，没有传统意义上的「宕机」',
  },
  {
    icon: 'shield',
    title: '数据分布式留存',
    desc: '资料与影像跨区域多副本存储，单一介质损坏不会带走任何一份思念',
  },
  {
    icon: 'moon',
    title: '昼夜持续在线',
    desc: '7×24 自动运维，无需停机维护，深夜的探望同样被稳稳接住',
  },
];

/**
 * 双重留存保障。
 *
 * 这两项回答的是两个完全不同的问题，缺一不可：
 *   链上存证   → 回答「内容有没有被动过手脚」
 *   纪念册导出 → 回答「如果这家平台不在了，我还能不能看到」
 *
 * 只做其中一个都会留下缺口：仅有存证，平台关停后你仍然打不开纪念馆；
 * 仅有导出，则无法向他人证明这份内容从未被篡改。
 *
 * 文案同样避开「永久」这类无法兑现的绝对化表述。
 */
const PILLARS = [
  {
    icon: 'chain',
    tag: 'BLOCKCHAIN',
    title: '链上存证',
    highlight: '内容指纹锚定以太坊',
    desc: '纪念馆的内容指纹写入 Base 链，并随区块最终锚定至以太坊主网。全球数十万个节点各自保存一份记录，任何机构都无法单独修改或抹去它——你可以随时在区块浏览器上自行查验。',
    points: ['只上链指纹，不含任何个人信息', '可在 BaseScan 自行查验', '任何单一机构无法篡改'],
  },
  {
    icon: 'box',
    tag: 'EXPORT',
    title: '纪念册导出',
    highlight: '随时可以完整带走',
    desc: '一键把整座纪念馆打包成一个可独立打开的网页文件，包含生平、相册原图与全部追思留言。无需联网、无需服务器，双击即可打开。',
    points: ['传给孩子、存进网盘、刻成光盘', '离线可用，不依赖任何平台', '保留你选的版式与配色'],
  },
];

export default function Home() {
  const navigate = useNavigate();
  const [health, setHealth] = useState<HealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const loggedIn = Boolean(getToken());

  useEffect(() => {
    let cancelled = false;
    api<HealthData>('/health').then((res) => {
      if (cancelled) return;
      if (res.ok && res.data) setHealth(res.data);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-ink-100 via-ink-50 to-ink-100">
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-0 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-candle-300/20 blur-3xl"
      />

      <header className="relative mx-auto flex max-w-6xl items-center justify-between px-6 py-6">
        <Link to="/" className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-ink-900 text-sm text-candle-300">
            墓
          </span>
          <span className="text-lg font-medium tracking-wide text-ink-800">线上墓迪</span>
        </Link>
        <nav className="flex items-center gap-6 text-sm text-ink-600">
          <Link to="/heroes" className="transition hover:text-ink-900">
            英雄纪念堂
          </Link>
          <Link to="/leaderboard" className="transition hover:text-ink-900">
            孝心排行榜
          </Link>
          <Link to="/articles" className="transition hover:text-ink-900">
            追思文章
          </Link>
          {loggedIn ? (
            <Link to="/memorials" className="transition hover:text-ink-900">
              我的纪念馆
            </Link>
          ) : (
            <Link to="/login" className="transition hover:text-ink-900">
              登录
            </Link>
          )}
        </nav>
      </header>

      <main className="relative mx-auto max-w-6xl px-6 pb-24 pt-16 sm:pt-24">
        <div className="mx-auto max-w-3xl text-center">
          <p className="animate-fade-up text-sm tracking-[0.3em] text-ink-500">ONLINE MEMORIAL</p>

          <h1 className="mt-6 animate-fade-up font-serifcn text-4xl leading-tight text-ink-900 sm:text-6xl"
            style={{ animationDelay: '80ms' }}>
            为远方的思念
            <br />
            留一处归宿
          </h1>

          <p className="mx-auto mt-8 max-w-xl animate-fade-up text-base leading-relaxed text-ink-600 sm:text-lg"
            style={{ animationDelay: '160ms' }}>
            无论相隔多远，都能为逝去的亲人建立一座纪念馆。
            <br className="hidden sm:block" />
            敬一支香、点一盏灯，让思念有处安放。
          </p>

          <div className="mt-12 flex animate-fade-up flex-col items-center justify-center gap-4 sm:flex-row"
            style={{ animationDelay: '240ms' }}>
            <button
              onClick={() => navigate(loggedIn ? '/create' : '/login')}
              className="group inline-flex h-12 items-center gap-2 rounded-full bg-ink-900 px-8 text-sm font-medium text-ink-50 shadow-lg shadow-ink-900/10 transition hover:bg-ink-800"
            >
              创建纪念馆
              <span className="transition group-hover:translate-x-1">→</span>
            </button>
            <Link
              to={loggedIn ? '/memorials' : '/login'}
              className="inline-flex h-12 items-center rounded-full border border-ink-300 px-8 text-sm text-ink-700 transition hover:border-ink-500 hover:bg-ink-100"
            >
              浏览纪念馆
            </Link>
          </div>

          <div className="mt-8 flex justify-center">
            <Link
              to="/heroes"
              className="text-sm text-ink-600 underline decoration-ink-300 underline-offset-4 transition hover:text-ink-900"
            >
              瞻仰英雄纪念堂 · 为先烈点一盏灯
            </Link>
          </div>
        </div>

        {/* 技术守护 */}
        <div className="mx-auto mt-20 max-w-2xl animate-fade-up" style={{ animationDelay: '320ms' }}>
          <p className="text-center text-xs tracking-[0.3em] text-ink-400">TECHNOLOGY</p>
          <h2 className="mt-4 text-center font-serifcn text-2xl text-ink-900">
            让思念，长久留存
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-center text-xs leading-relaxed text-ink-500">
            纪念馆不寄托于某一台具体的机器，而是运行在遍布全球的边缘网络上，
            没有单点故障。更进一步——我们把内容指纹写进区块链，
            同时让你随时能把整座纪念馆完整带走。
          </p>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {TECH_FEATURES.map((f) => (
              <div
                key={f.title}
                className="rounded-2xl border border-ink-200 bg-white/70 p-5 backdrop-blur transition hover:border-ink-300"
              >
                <Icon name={f.icon} className="text-xl" />
                <p className="mt-2.5 text-sm text-ink-900">{f.title}</p>
                <p className="mt-1.5 text-xs leading-relaxed text-ink-500">{f.desc}</p>
              </div>
            ))}
          </div>

          {/* 双重留存保障：链上存证 + 纪念册导出 */}
          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {PILLARS.map((p) => (
              <div
                key={p.title}
                className="relative overflow-hidden rounded-2xl border border-ink-300 bg-white/85 p-6 backdrop-blur transition hover:border-ink-400"
              >
                <span className="absolute right-5 top-5 font-mono text-[9px] tracking-[0.2em] text-ink-300">
                  {p.tag}
                </span>

                <Icon name={p.icon} className="text-2xl" />
                <p className="mt-3 font-serifcn text-base text-ink-900">{p.title}</p>
                <p className="mt-0.5 text-[11px] text-candle-600">{p.highlight}</p>

                <p className="mt-3 text-xs leading-relaxed text-ink-500">{p.desc}</p>

                <ul className="mt-4 space-y-1.5">
                  {p.points.map((pt) => (
                    <li key={pt} className="flex gap-2 text-[11px] text-ink-600">
                      <span className="mt-[3px] h-1 w-1 shrink-0 rounded-full bg-candle-400" />
                      {pt}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* 实时运行状态 */}
          <div className="mt-6 flex items-center justify-center gap-2.5 rounded-2xl border border-ink-200 bg-white/70 py-3.5 backdrop-blur">
            <span
              className={`h-2 w-2 rounded-full ${
                loading ? 'bg-ink-300' : health ? 'bg-emerald-500' : 'bg-red-500'
              } ${health && !loading ? 'animate-breathe' : ''}`}
            />
            <span className="text-xs text-ink-600">
              {loading ? '正在接入全球网络…' : health ? '全球节点守护中' : '连接异常'}
            </span>
            {!loading && health && (
              <span className="ml-1 font-mono text-[10px] text-ink-400">
                {health.status === 'healthy' ? 'ALL SYSTEMS OPERATIONAL' : health.status}
              </span>
            )}
          </div>

          <p className="mt-4 text-center text-[10px] text-ink-400">
            基于 Cloudflare 全球边缘网络构建 · 无服务器架构 · 存证锚定以太坊主网
          </p>
        </div>
      </main>

      <footer className="relative py-10 text-center text-xs text-ink-400">
        <p>愿思念有归处 · 线上墓迪</p>
      </footer>
    </div>
  );
}


